import React from 'react';
import Select from 'react-select';

/**
 * CustomMultiSelect
 * ------------------------------------------------------------------
 * Wrapper sobre react-select que mantiene el mismo "look & feel"
 * de tu CustomSelect (mismas variables CSS de tu design system:
 * cs-surface, cs-dark, cs-border, cs-primary, cs-danger, cs-muted).
 *
 * Uso con Formik:
 *
 *   <CustomMultiSelect
 *      label="Assign users"
 *      name="users"
 *      placeholder="Select users…"
 *      options={users}                       // [{id, name}]
 *      value={validation.values.users}       // array de ids
 *      setFieldValue={validation.setFieldValue}
 *      error={validation.errors.users}
 *   />
 */
const CustomMultiSelect = ({
  value = [],
  name,
  options = [],
  setFieldValue,
  label,
  labelClassName = '',
  className = '',
  error,
  placeholder,
  isClearable = true,
  isSearchable = true,
  ...props
}) => {

  // CSS vars helper (lee tus variables del :root para que combine con CustomSelect)
  const css = (token, fallback) =>
    `var(--${token}, ${fallback})`;

  // Mapeamos la lista de "ids" del form al shape que necesita react-select
  const selectedOptions = options
    .filter((opt) => value?.includes(opt.id))
    .map((opt) => ({ value: opt.id, label: opt.name }));

  const allOptions = options.map((opt) => ({
    value: opt.id,
    label: opt.name,
  }));

  const handleChange = (selected) => {
    const ids = selected ? selected.map((s) => s.value) : [];
    setFieldValue?.(name, ids);
  };

  // Estilos pensados para que combinen con CustomSelect (h-12, rounded-md, etc.)
  const styles = {
    control: (base, state) => ({
      ...base,
      minHeight: '3rem',           // h-12
      borderRadius: '0.375rem',     // rounded-md
      backgroundColor: css('cs-surface', '#ffffff'),
      borderColor: error
        ? css('cs-danger', '#ef4444')
        : state.isFocused
        ? css('cs-primary', '#2563eb')
        : css('cs-border', '#e2e8f0'),
      boxShadow: state.isFocused
        ? `0 0 0 2px ${
            error
              ? 'rgba(239,68,68,0.30)'
              : 'rgba(37,99,235,0.30)'
          }`
        : '0 1px 2px 0 rgba(0,0,0,0.05)', // shadow-sm
      paddingLeft: '0.5rem',
      paddingRight: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
      '&:hover': {
        borderColor: error
          ? css('cs-danger', '#ef4444')
          : css('cs-primary', '#2563eb'),
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '2px 4px',
      gap: '4px',
    }),
    placeholder: (base) => ({
      ...base,
      color: css('cs-muted', '#94a3b8'),
      fontSize: '0.875rem',
    }),
    input: (base) => ({
      ...base,
      color: css('cs-dark', '#0f172a'),
      fontSize: '0.875rem',
      margin: 0,
      padding: 0,
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: css('cs-primary-soft', '#eef4ff'),
      borderRadius: '9999px',
      paddingLeft: '4px',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: css('cs-primary-dark', '#1d4ed8'),
      fontSize: '0.75rem',
      fontWeight: 600,
    }),
    multiValueRemove: (base) => ({
      ...base,
      borderRadius: '9999px',
      color: css('cs-primary-dark', '#1d4ed8'),
      ':hover': {
        backgroundColor: css('cs-primary', '#2563eb'),
        color: '#fff',
      },
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '0.5rem',
      overflow: 'hidden',
      border: `1px solid ${css('cs-border', '#e2e8f0')}`,
      boxShadow: '0 8px 32px rgba(15, 23, 42, 0.10)',
      zIndex: 50,
    }),
    menuList: (base) => ({
      ...base,
      padding: '4px',
    }),
    option: (base, state) => ({
      ...base,
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      padding: '8px 10px',
      cursor: 'pointer',
      backgroundColor: state.isSelected
        ? css('cs-primary', '#2563eb')
        : state.isFocused
        ? css('cs-primary-soft', '#eef4ff')
        : 'transparent',
      color: state.isSelected ? '#fff' : css('cs-dark', '#0f172a'),
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: css('cs-muted', '#94a3b8'),
      ':hover': { color: css('cs-dark', '#0f172a') },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: css('cs-muted', '#94a3b8'),
      ':hover': { color: css('cs-danger', '#ef4444') },
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: css('cs-border', '#e2e8f0'),
    }),
  };

  return (
    <div className={`flex flex-col w-full py-2 gap-1 ${className}`}>
      {label && (
        <label className={`text-sm font-medium text-cs-dark ${labelClassName}`}>
          {label}
        </label>
      )}

      <Select
        isMulti
        isClearable={isClearable}
        isSearchable={isSearchable}
        name={name}
        options={allOptions}
        value={selectedOptions}
        onChange={handleChange}
        placeholder={placeholder ?? 'Select…'}
        noOptionsMessage={() => 'No options found'}
        styles={styles}
        menuPlacement="auto"
        classNamePrefix="cs-select"
        {...props}
      />

      {error && (
        <span className="mt-1 text-sm text-cs-danger">{error}</span>
      )}
    </div>
  );
};

export default CustomMultiSelect;
